import {
  formatPublicTransportConfiguration,
  PublicTransportConfiguration,
  formatHighwaysConfiguration,
  formatParkingConfiguration,
  HighwaysConfiguration,
  ParkingConfiguration,
} from '@shared/components';
import {
  OnboardingSavePublicTransportDto,
  OnboardingSaveHighwaysDto,
  OnboardingSaveParkingDto,
  CompanyServicesResponse,
  ServiceType,
} from '@interfaces/models';
import { FormControl, FormMessage, FormField, FormItem } from '@ui/components';
import { FieldValues, Control, Path } from 'react-hook-form';
import { useCallback, useEffect, useMemo } from 'react';
import { ZoneType, CityDto } from '@interfaces/types';
import { Loading } from '@ui/components';
import { cn } from '@ui/utils';

import { useCreateEmployeesProfileServicesData } from './use-create-employees-profile-services-data';

interface CreateEmployeesProfileServicesProps {
  setHasAnyConfigurationEditing?: (hasAnyConfigurationEditing: boolean) => void;
  onChange?: (config: CompanyServicesResponse) => void;
  value?: CompanyServicesResponse;
  className?: string;
}

interface FormCreateEmployeesProfileServicesProps<T extends FieldValues = FieldValues>
  extends Omit<CreateEmployeesProfileServicesProps, 'onChange' | 'value'> {
  className?: string;
  formControl: Control<T>;
  hideErrorMessage?: boolean;
  name: Path<T>;
  required?: boolean;
}

const nameCompare = <T extends Record<'name', string>>(a: T, b: T) =>
  a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase());

export const CreateEmployeesProfileServices = ({
  setHasAnyConfigurationEditing,
  className,
  onChange,
  value,
}: CreateEmployeesProfileServicesProps) => {
  const {
    setIsPublicTransportConfigurationEditing,
    setIsHighwaysConfigurationEditing,
    setIsParkingConfigurationEditing,
    isAnyConfigurationEditing,
    isGeneralConfigLoading,
    availableServices,
    companyPolicyData,
    isQueryLoading,
  } = useCreateEmployeesProfileServicesData();

  const cities = useMemo(
    () => companyPolicyData?.cities?.sort(nameCompare) ?? [],
    [companyPolicyData],
  );

  const zones = useMemo(
    () => companyPolicyData?.zones?.sort(nameCompare) ?? [],
    [companyPolicyData],
  );

  const getCitiesByZoneType = useCallback(
    (zoneType: ZoneType): CityDto[] => {
      return (
        cities
          .filter((city) => {
            const cityCode = city.code;
            return zones.some((zone) => zone.type === zoneType && zone.cityCode === cityCode);
          })
          .sort(nameCompare) ?? []
      );
    },
    [cities, zones],
  );

  const getZonesOfType = useCallback(
    (type: ZoneType) => zones.filter((zone) => zone.type === type),
    [zones],
  );

  const handleParkingConfigChange = useCallback(
    (config: OnboardingSaveParkingDto | null) => {
      if (onChange) {
        onChange({
          ...value,
          parkingConfiguration: formatParkingConfiguration(config),
        } as CompanyServicesResponse);
      }
    },
    [onChange, value],
  );

  const handleHighwaysConfigChange = useCallback(
    (config: OnboardingSaveHighwaysDto | null) => {
      if (onChange) {
        onChange({
          ...value,
          highwaysConfiguration: formatHighwaysConfiguration(config),
        } as CompanyServicesResponse);
      }
    },
    [onChange, value],
  );

  const handlePublicTransportConfigChange = useCallback(
    (config: OnboardingSavePublicTransportDto | null) => {
      if (onChange) {
        onChange({
          ...value,
          publicTransportConfiguration: formatPublicTransportConfiguration(config),
        } as CompanyServicesResponse);
      }
    },
    [onChange, value],
  );

  useEffect(
    () => setHasAnyConfigurationEditing?.(isAnyConfigurationEditing),
    [isAnyConfigurationEditing, setHasAnyConfigurationEditing],
  );

  if (isQueryLoading || isGeneralConfigLoading) return <Loading />;

  return (
    <>
      <div className={cn('space-y-6', className)}>
        {availableServices.includes(ServiceType.PARKINGS) && (
          <ParkingConfiguration
            className="bg-background border border-border-weak outline-none"
            initialConfiguration={value?.parkingConfiguration}
            onConfigurationChange={handleParkingConfigChange}
            setIsEditing={setIsParkingConfigurationEditing}
            cities={getCitiesByZoneType(ZoneType.PARKING)}
            zones={getZonesOfType(ZoneType.PARKING)}
            days={companyPolicyData?.days ?? []}
          />
        )}
        {availableServices.includes(ServiceType.HIGHWAYS) && (
          <HighwaysConfiguration
            className="bg-background border border-border-weak outline-none"
            highwaySections={companyPolicyData?.highwaySections ?? []}
            initialConfiguration={value?.highwaysConfiguration}
            onConfigurationChange={handleHighwaysConfigChange}
            setIsEditing={setIsHighwaysConfigurationEditing}
            days={companyPolicyData?.days ?? []}
          />
        )}
        {availableServices.includes(ServiceType.PUBLIC_TRANSPORT) && (
          <PublicTransportConfiguration
            className="bg-background border border-border-weak outline-none"
            initialConfiguration={value?.publicTransportConfiguration}
            onConfigurationChange={handlePublicTransportConfigChange}
            cities={getCitiesByZoneType(ZoneType.PUBLIC_TRANSPORT)}
            setIsEditing={setIsPublicTransportConfigurationEditing}
            zones={getZonesOfType(ZoneType.PUBLIC_TRANSPORT)}
            days={companyPolicyData?.days ?? []}
          />
        )}
      </div>
    </>
  );
};

export function FormCreateEmployeesProfileServices<T extends FieldValues>({
  setHasAnyConfigurationEditing,
  hideErrorMessage = false,
  formControl,
  className,
  name,
}: FormCreateEmployeesProfileServicesProps<T>) {
  return (
    <FormField
      render={({ field }) => {
        const handleChange = (config: CompanyServicesResponse) => {
          field.onChange(config);
        };

        return (
          <FormItem className={cn('relative z-0', className)}>
            <FormControl>
              <CreateEmployeesProfileServices
                setHasAnyConfigurationEditing={setHasAnyConfigurationEditing}
                onChange={(config) => handleChange(config)}
                className={className}
                value={field.value}
              />
            </FormControl>
            {!hideErrorMessage && <FormMessage className="mt-2" />}
          </FormItem>
        );
      }}
      control={formControl}
      name={name}
    />
  );
}
